import {
    Component,
    h,
    Prop,
    ComponentInterface,
    Host,
    EventEmitter,
    Event,
    Method,
    Element,
    State,
    Watch,
} from "@stencil/core";
import { ChevronLeft, ChevronRight } from "../icons";
import { MenuItem, MenuItemEventDetail } from "./interface";

@Component({
    tag: "uic-horizontal-menu-container",
    styleUrl: "horizontal-menu-container.scss",
    shadow: true, //Workaround to solve scss problems if one uic-tab-list (e. g. vertical) is used inside other uic-tab-list (e. g. horizontal).
    //After this problem of uic-tab-list has been solved can 'shadow: true' be deleted.
})
export class HorizontalMenuContainer implements ComponentInterface {
    /**
     * This is the complete, nested menu tree. A menu item always contains a name that will be shown on the tab.
     * Additionally it either contains a link (e.g. for an iframe) or another menu tree for the next menu level.
     * If both are present, the menu tree gets chosen while the link gets ignored.
     */
    @Prop() menuTree: MenuItem[];

    /**
     * Name of the setting that should be selected initially. If there is a first level setting with an equal name it opens automatically.
     */
    @Prop() preselectedMenuItem: string;

    /**
     * Event gets emitted when a leaf item (no child MenuItems) gets clicked.
     */
    @Event() uicOpenMenuItem: EventEmitter<MenuItemEventDetail>;

    /**
     * Boolean for each level if this level should be visible. The first one is always visible. Currently the length of this array determines the number of levels.
     */
    @State() showFlyoutLevel: boolean[] = [true, false, false, false];

    /**
     * The selected item for each level. null means no item is selected on this level.
     */
    @State() selectedItem: MenuItem[] = [null, null, null, null];

    /**
     * Block any menu input if it is prohibited, for example if there are unsaved changes
     */
    @Prop() preventAutoNavigation: boolean = false;

    /**
     * When clicking a leaf item, choose whether the submenus should collapse or stay open
     */
    @Prop() closeSubmenuAfterSelection: boolean = true;

    @Element() host: HTMLUicHorizontalMenuContainerElement;

    private lastLevelClicked: number = 1;
    private lastItemClicked: MenuItem;
    private lastClickEvent: Event;
    private openedSettingsHistory: MenuItem[];

    /**
     * Refresh the selection (and selction indicator).
     * Opening the settings for the first time, a tab with the same name as preselectedMenuItem gets selected.
     * Other times the currently selected menu gets re-selected. Otherwise the selection indicator won't show up.
     */
    @Method() async refresh() {
        this.menuTree = [...this.menuTree];
        this.openedSettingsHistory = null;
        if (!this.selectedItem[0]) {
            setTimeout(() => {
                const tabs = this.host.shadowRoot.querySelectorAll("uic-tab");
                const preselectedMenuValue = this.preselectedMenuItem.replace(
                    /\s/g,
                    ""
                );
                for (let tab of tabs) {
                    if (tab.value === preselectedMenuValue) {
                        tab.click();
                        const firstFlyoutMenu: HTMLUicTabListElement = this.host.shadowRoot.querySelector(
                            "uic-tab-list"
                        );
                        firstFlyoutMenu.updateSelectionIndicator(tab);
                        break;
                    }
                }
            }, 10);
        } else {
            setTimeout(() => {
                const tab: HTMLUicTabElement = this.host.shadowRoot.querySelector(
                    "uic-tab[selected]"
                );
                const firstFlyoutMenu: HTMLUicTabListElement = this.host.shadowRoot.querySelector(
                    "uic-tab-list"
                );
                firstFlyoutMenu.updateSelectionIndicator(tab);
            }, 10);
        }
    }

    @Watch("menuTree")
    onMenuTreeChange() {
        let treeDepth = 0;
        for (let item of this.menuTree) {
            const depth = this.maxDepth(item.target);
            if (depth > treeDepth) treeDepth = depth;
        }
        this.showFlyoutLevel = new Array<boolean>(treeDepth);
        this.setVisibleUntil(0);
        this.selectedItem = new Array<MenuItem>(treeDepth);
        this.setSelectedItem(null, 0);
    }

    maxDepth(target: string | MenuItem[]): number {
        if (Array.isArray(target) && target.length > 0) {
            let subDepth = Math.max(
                ...target.map((i) => this.maxDepth(i.target))
            );
            return subDepth + 1;
        } else {
            return 1;
        }
    }

    private handleKeydown(item: MenuItem, level: number, event: KeyboardEvent) {
        if (event.key === "Enter" || event.key === " ") {
            this.openIfAllowed(item, level, event);
        }
    }

    setVisibleUntil(level: number) {
        for (let levelCounter = 0; levelCounter <= level; levelCounter++)
            this.showFlyoutLevel[levelCounter] = true;

        for (
            let levelCounter = level + 1;
            levelCounter < this.showFlyoutLevel.length;
            levelCounter++
        )
            this.showFlyoutLevel[levelCounter] = false;

        this.showFlyoutLevel = [...this.showFlyoutLevel];
    }

    removeItemIfInvisible() {
        let lastVisibleLevel = 0;
        for (let level = 0; level < this.showFlyoutLevel.length; level++) {
            if (this.showFlyoutLevel[level] === true) {
                lastVisibleLevel = level;
            } else break;
        }
        this.setSelectedItem(null, lastVisibleLevel);
    }

    setVisibilityIfPathSelected() {
        for (let level = 0; level < this.selectedItem.length - 1; level++) {
            if (
                this.selectedItem[level + 1] ||
                (this.selectedItem[level] &&
                    this.isMenuItemArray(this.selectedItem[level].target) &&
                    this.selectedItem[level].target.length > 0)
            ) {
                this.showFlyoutLevel[level + 1] = true;
            } else break;
        }
        this.showFlyoutLevel = [...this.showFlyoutLevel];
    }

    isMenuItemArray(object: string | MenuItem[]): object is MenuItem[] {
        return (object as MenuItem[])[0].target !== undefined;
    }

    setSelectedItem(item: MenuItem, level: number) {
        this.selectedItem[level] = item;
        const flyoutMenus = this.host.shadowRoot.querySelectorAll(
            "uic-tab-list"
        );
        if (flyoutMenus.length <= 0) return;
        flyoutMenus[level].value = item ? this.getMenuItemValue(item) : null;
        for (
            let levelCounter = level + 1;
            levelCounter < this.selectedItem.length &&
            levelCounter < flyoutMenus.length;
            levelCounter++
        ) {
            this.selectedItem[levelCounter] = null;
            (flyoutMenus[levelCounter] as HTMLUicTabListElement).value = null;
        }

        this.selectedItem = [...this.selectedItem];
    }

    restoreHistory() {
        const flyoutMenus = this.host.shadowRoot.querySelectorAll(
            "uic-tab-list"
        );
        this.openedSettingsHistory.forEach((item, level) => {
            if (!item) return;
            this.setVisibleUntil(level);
            this.setSelectedItem(item, level);
            (flyoutMenus[
                level
            ] as HTMLUicTabListElement).value = this.getMenuItemValue(item);
        });
    }

    openItemOfLevel(item: MenuItem, level: number) {
        if (this.showFlyoutLevel[level + 1]) {
            if (item === this.selectedItem[level]) {
                this.removeItemIfInvisible();
                const firstFlyoutMenu = this.host.shadowRoot.querySelectorAll(
                    "uic-tab-list"
                );
                firstFlyoutMenu[level].updateSelectionIndicator(null);
            }
            this.setVisibleUntil(level);

            if (item === this.selectedItem[level]) {
                if (level === 0 && this.openedSettingsHistory) {
                    this.selectedItem = [...this.openedSettingsHistory];
                    this.setSelectedItem(this.openedSettingsHistory[0], 0);
                    const firstFlyoutMenu: HTMLUicTabListElement = this.host.shadowRoot.querySelector(
                        "uic-tab-list"
                    );
                    firstFlyoutMenu.value = this.getMenuItemValue(
                        this.openedSettingsHistory[0]
                    );
                    const tab: HTMLUicTabElement = firstFlyoutMenu.querySelector(
                        `uic-tab[value="${this.getMenuItemValue(
                            this.openedSettingsHistory[0]
                        )}"]`
                    );
                    setTimeout(() => {
                        firstFlyoutMenu.updateSelectionIndicator(tab);
                    }, 10);
                } else if (level === 0) {
                    const firstFlyoutMenu: HTMLUicTabListElement = this.host.shadowRoot.querySelector(
                        "uic-tab-list"
                    );
                    (firstFlyoutMenu.querySelector(
                        "uic-tab[selected]"
                    ) as HTMLUicTabElement).selected = false;
                }
                return;
            }
        }

        if (
            level === 0 &&
            this.openedSettingsHistory &&
            item === this.openedSettingsHistory[0]
        ) {
            this.restoreHistory();
            return;
        }

        if (item === this.selectedItem[level]) {
            this.setVisibilityIfPathSelected();
            const firstFlyoutMenu = this.host.shadowRoot.querySelectorAll(
                "uic-tab-list"
            );
            const tab: HTMLUicTabElement = this.host.shadowRoot.querySelector(
                `uic-tab[value="${this.getMenuItemValue(item)}"]`
            );
            firstFlyoutMenu[level].updateSelectionIndicator(tab);
            return;
        }

        this.setSelectedItem(item, level);

        if (this.isMenuItemArray(item.target) && item.target.length > 0)
            this.setVisibleUntil(level + 1);
        else this.openItem(item);
    }

    openItem(item: MenuItem) {
        if (this.closeSubmenuAfterSelection) {
            this.setVisibleUntil(0);
        }
        this.openedSettingsHistory = [...this.selectedItem];
        if (!this.preventAutoNavigation)
            this.uicOpenMenuItem.emit({ menuItemData: item });
    }

    removeFocusBorder() {
        const tabs = this.host.shadowRoot.querySelectorAll("uic-tab");
        tabs.forEach((tab) => {
            tab.classList.remove("uic-tab:focus");
        });
    }

    openIfAllowed(item: MenuItem, level: number, event: Event) {
        if (this.preventAutoNavigation) {
            this.lastLevelClicked = level;
            this.lastItemClicked = item;
            this.lastClickEvent = event;
            this.removeFocusBorder();
            this.uicOpenMenuItem.emit({ menuItemData: item });
            return;
        }

        this.openItemOfLevel(item, level);
    }

    private getMenuItemValue(menuItem: MenuItem) {
        if (!menuItem.value) {
            menuItem.value = menuItem.displayName.replace(/\s/g, "");
        }
        return menuItem.value;
    }

    /**
     * If an input was prevented by preventAutoNavigation, the parent component can call this function to resume the functionality.
     */
    @Method() async selectClickedMenuItem() {
        if (!this.lastItemClicked) return;
        const flyoutMenus = this.host.shadowRoot.querySelectorAll(
            "uic-tab-list"
        );

        this.openItemOfLevel(this.lastItemClicked, this.lastLevelClicked);
        flyoutMenus[this.lastLevelClicked].delayedOnClick(this.lastClickEvent);
    }

    render() {
        return (
            <Host>
                <div class="uic-horizontal-menu-container__shadow-root">
                    <uic-tab-list
                        class="uic-horizontal-menu-container__tab-list__main"
                        preventAutoNavigation={this.preventAutoNavigation}
                        variant="sub"
                        vertical
                    >
                        {this.menuTree.map((item) => (
                            <div class="uic-horizontal-menu-container__icon__wrapper">
                                <uic-tab
                                    label={item.displayName}
                                    value={this.getMenuItemValue(item)}
                                    class={
                                        this.selectedItem[0] === item
                                            ? "uic-horizontal-menu-container__tab__mainmenu-selected"
                                            : "uic-horizontal-menu-container__tab__mainmenu"
                                    }
                                    selected={item === this.selectedItem[0]}
                                    onClick={(event) =>
                                        this.openIfAllowed(item, 0, event)
                                    }
                                    onKeyDown={(event) =>
                                        this.handleKeydown(item, 0, event)
                                    }
                                ></uic-tab>
                                {this.isMenuItemArray(item.target) &&
                                item.target.length > 0 ? (
                                    this.selectedItem[0] === item &&
                                    this.showFlyoutLevel[1] ? (
                                        <span class="uic-horizontal-menu-container__icon__wrapper__icon">
                                            {" "}
                                            {ChevronLeft()}{" "}
                                        </span>
                                    ) : (
                                        <span class="uic-horizontal-menu-container__icon__wrapper__icon">
                                            {" "}
                                            {ChevronRight()}{" "}
                                        </span>
                                    )
                                ) : (
                                    <div></div>
                                )}
                            </div>
                        ))}
                    </uic-tab-list>

                    {this.showFlyoutLevel
                        .slice(1, this.showFlyoutLevel.length)
                        .map((_, index) => (
                            <uic-tab-list
                                class={
                                    this.showFlyoutLevel[index + 1]
                                        ? this.showFlyoutLevel[index + 2]
                                            ? "uic-horizontal-menu-container__tab-list__flyout"
                                            : "uic-horizontal-menu-container__tab-list__flyout-last"
                                        : "uic-horizontal-menu-container__tab-list__hidden"
                                }
                                preventAutoNavigation={
                                    this.preventAutoNavigation
                                }
                                variant="content"
                                vertical
                            >
                                {this.selectedItem[index] &&
                                this.isMenuItemArray(
                                    this.selectedItem[index].target
                                ) ? (
                                    (this.selectedItem[index]
                                        .target as MenuItem[]).map((item) => (
                                        <div class="uic-horizontal-menu-container__icon__wrapper">
                                            <uic-tab
                                                label={item.displayName}
                                                class={
                                                    item ===
                                                    this.selectedItem[index + 1]
                                                        ? this.showFlyoutLevel[
                                                              index + 2
                                                          ]
                                                            ? "uic-horizontal-menu-container__tab__submenu-selected"
                                                            : "uic-horizontal-menu-container__tab__submenu-selected-last"
                                                        : this.showFlyoutLevel[
                                                              index + 2
                                                          ]
                                                        ? "uic-horizontal-menu-container__tab__submenu"
                                                        : "uic-horizontal-menu-container__tab__submenu-last"
                                                }
                                                value={this.getMenuItemValue(
                                                    item
                                                )}
                                                selected={
                                                    item ===
                                                    this.selectedItem[index + 1]
                                                }
                                                onClick={(event) =>
                                                    this.openIfAllowed(
                                                        item,
                                                        index + 1,
                                                        event
                                                    )
                                                }
                                                onKeyDown={(event) =>
                                                    this.handleKeydown(
                                                        item,
                                                        index + 1,
                                                        event
                                                    )
                                                }
                                            ></uic-tab>
                                            {this.isMenuItemArray(
                                                item.target
                                            ) && item.target.length > 0 ? (
                                                this.selectedItem[index + 1] ===
                                                    item &&
                                                this.showFlyoutLevel[
                                                    index + 2
                                                ] ? (
                                                    <span class="uic-horizontal-menu-container__icon__wrapper__icon">
                                                        {" "}
                                                        {ChevronLeft()}{" "}
                                                    </span>
                                                ) : (
                                                    <span class="uic-horizontal-menu-container__icon__wrapper__icon">
                                                        {" "}
                                                        {ChevronRight()}{" "}
                                                    </span>
                                                )
                                            ) : (
                                                <div></div>
                                            )}
                                        </div>
                                    ))
                                ) : (
                                    <div></div>
                                )}
                            </uic-tab-list>
                        ))}
                </div>
            </Host>
        );
    }
}
